/**
 * Feed API — Firestore CRUD for Social Feed
 * 
 * Storage pattern: Subcollections under the project document
 *   organizations/{orgId}/projects/{projectId}/feed/{postId}           → FeedPost
 *   organizations/{orgId}/projects/{projectId}/feedComments/{commentId} → FeedComment
 * 
 * This follows the standard project subcollection pattern.
 */

import {
    collection,
    onSnapshot,
    query,
    orderBy,
    limit,
    type CollectionReference,
    
    Unsubscribe,
} from 'firebase/firestore';
import { db } from '@/firebase-config';
import { FeedPost, FeedComment, PostType, MediaAttachment, ContentLink, Mention, FeedReaction, FeedReactionKind, FeedReactionTarget } from './types';
import { logger } from '@/utils/logger';
import { getEffectiveUserId } from '../auth/current-user-identity';
import { buildProjectSubcollectionPath, type ProjectScope } from '../sync/project-scope';
import { enqueueStructuredEntityWrite, type StructuredEntityWriteRegistration } from '../sync/structured-entity-write';
import {
    markPendingFeedCommentWrite,
    markPendingFeedPostWrite,
    markPendingFeedReactionWrite,
} from '../sync/feed-pending-writes';

const FEED_SUBCOLLECTION = 'feed';
const COMMENTS_SUBCOLLECTION = 'feedComments';
const REACTIONS_SUBCOLLECTION = 'feedReactions';

// ---------------------
// Helpers
// ---------------------

/**
 * Recursively removes undefined values from an object for Firestore compatibility.
 * Duplicated from project-api to keep feed-api self-contained.
 */
function sanitizeForFirestore<T>(obj: T): T {
    if (obj === undefined) return null as T;
    if (obj === null || typeof obj !== 'object') return obj;
    if (Array.isArray(obj)) {
        return obj.map((item: unknown) => sanitizeForFirestore(item)) as T;
    }
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
        if (value !== undefined) {
            result[key] = sanitizeForFirestore(value);
        }
    }
    return result as T;
}

function feedRef(scope: ProjectScope) {
    return collection(
        db,
        buildProjectSubcollectionPath(scope, FEED_SUBCOLLECTION)
    ) as CollectionReference<Omit<FeedPost, 'id'>>;
}

function commentsRef(scope: ProjectScope) {
    return collection(
        db,
        buildProjectSubcollectionPath(scope, COMMENTS_SUBCOLLECTION)
    ) as CollectionReference<Omit<FeedComment, 'id'>>;
}

function reactionsRef(scope: ProjectScope) {
    return collection(
        db,
        buildProjectSubcollectionPath(scope, REACTIONS_SUBCOLLECTION)
    ) as CollectionReference<Omit<FeedReaction, 'id'>>;
}

const FEED_POST_WRITE_REGISTRATION: StructuredEntityWriteRegistration = {
    feature: 'admin_adjacent',
    capability: 'MANAGE_FEED',
    conflictPrefix: FEED_SUBCOLLECTION,
    allowedKinds: ['patchEntity'],
    authErrorMessage: 'Authentication required to preserve feed project writes',
};

const FEED_COMMENT_WRITE_REGISTRATION: StructuredEntityWriteRegistration = {
    feature: 'admin_adjacent',
    capability: 'MANAGE_FEED',
    conflictPrefix: COMMENTS_SUBCOLLECTION,
    allowedKinds: ['patchEntity'],
    authErrorMessage: 'Authentication required to preserve feed project writes',
};

const FEED_REACTION_WRITE_REGISTRATION: StructuredEntityWriteRegistration = {
    feature: 'admin_adjacent',
    capability: 'MANAGE_FEED',
    conflictPrefix: REACTIONS_SUBCOLLECTION,
    allowedKinds: ['patchEntity'],
    authErrorMessage: 'Authentication required to preserve feed project writes',
};

async function enqueueFeedPostPatch(
    scope: ProjectScope,
    postId: string,
    payload: Partial<FeedPost>
): Promise<void> {
    const actorId = requireFeedActorId();
    markPendingFeedPostWrite(scope, postId);
    await enqueueStructuredEntityWrite({
        scope,
        registration: FEED_POST_WRITE_REGISTRATION,
        entityId: postId,
        kind: 'patchEntity',
        payload: {
            ...payload,
            id: postId,
        },
        actorId,
    });
}

async function enqueueFeedCommentPatch(
    scope: ProjectScope,
    commentId: string,
    payload: Partial<FeedComment>
): Promise<void> {
    const actorId = requireFeedActorId();
    markPendingFeedCommentWrite(scope, commentId);
    await enqueueStructuredEntityWrite({
        scope,
        registration: FEED_COMMENT_WRITE_REGISTRATION,
        entityId: commentId,
        kind: 'patchEntity',
        payload: {
            ...payload,
            id: commentId,
        },
        actorId,
    });
}

async function enqueueFeedReactionPatch(
    scope: ProjectScope,
    reactionId: string,
    payload: Partial<FeedReaction>
): Promise<void> {
    const actorId = requireFeedActorId();
    markPendingFeedReactionWrite(scope, reactionId);
    await enqueueStructuredEntityWrite({
        scope,
        registration: FEED_REACTION_WRITE_REGISTRATION,
        entityId: reactionId,
        kind: 'patchEntity',
        payload: {
            ...payload,
            id: reactionId,
        },
        actorId,
    });
}

function requireFeedActorId(): string {
    const actorId = getEffectiveUserId();
    if (!actorId) {
        throw new Error('Authentication required to preserve feed project writes');
    }
    return actorId;
}

// ---------------------
// Post CRUD
// ---------------------

export interface CreatePostParams {
    id?: string;
    authorId: string;
    authorName: string;
    content: string;
    postType?: PostType;
    media?: MediaAttachment[];
    contentLinks?: ContentLink[];
    mentions?: Mention[];
    authorPhotoUrl?: string;
    createdAt?: number;
    updatedAt?: number;
}

/**
 * Creates a new feed post in Firestore.
 * Returns the generated document ID.
 */
export async function createFeedPost(scope: ProjectScope, params: CreatePostParams): Promise<string> {
    const now = Date.now();
    const postId = params.id ?? crypto.randomUUID();
    const postData: FeedPost = {
        id: postId,
        authorId: params.authorId,
        authorName: params.authorName,
        ...(params.authorPhotoUrl ? { authorPhotoUrl: params.authorPhotoUrl } : {}),
        postType: params.postType || 'text',
        content: params.content,
        media: params.media || [],
        contentLinks: params.contentLinks || [],
        mentions: params.mentions || [],
        pinned: false,
        createdAt: params.createdAt ?? now,
        updatedAt: params.updatedAt ?? params.createdAt ?? now,
        deleted: false,
    };

    await enqueueFeedPostPatch(scope, postId, sanitizeForFirestore(postData));
    logger.cloud(`Feed post preserved: ${postId}`);
    return postId;
}

/**
 * Updates an existing feed post.
 */
export async function updateFeedPost(
    scope: ProjectScope,
    postId: string,
    updates: Partial<FeedPost>
): Promise<void> {
    await enqueueFeedPostPatch(scope, postId, sanitizeForFirestore({
        ...updates,
        updatedAt: updates.updatedAt ?? Date.now(),
    }));
    logger.cloud(`Feed post update preserved: ${postId}`);
}

/**
 * Soft-deletes a feed post (sets deleted: true).
 */
export async function deleteFeedPost(
    scope: ProjectScope,
    postId: string,
    updatedAt = Date.now()
): Promise<void> {
    await enqueueFeedPostPatch(scope, postId, { deleted: true, updatedAt });
    logger.cloud(`Feed post soft-delete preserved: ${postId}`);
}

/**
 * Toggles the pinned status of a feed post.
 */
export async function toggleFeedPostPin(
    scope: ProjectScope,
    postId: string,
    pinned: boolean,
    updatedAt = Date.now()
): Promise<void> {
    await enqueueFeedPostPatch(scope, postId, { pinned, updatedAt });
    logger.cloud(`Feed post pin update preserved: ${postId} → ${pinned}`);
}

// ---------------------
// Comment CRUD
// ---------------------

export interface CreateCommentParams {
    id?: string;
    postId: string;
    authorId: string;
    authorName: string;
    content: string;
    mentions?: Mention[];
    createdAt?: number;
}

/**
 * Creates a new comment in Firestore.
 * Returns the generated document ID.
 */
export async function createFeedComment(scope: ProjectScope, params: CreateCommentParams): Promise<string> {
    const now = Date.now();
    const commentId = params.id ?? crypto.randomUUID();
    const commentData: FeedComment = {
        id: commentId,
        postId: params.postId,
        authorId: params.authorId,
        authorName: params.authorName,
        content: params.content,
        mentions: params.mentions || [],
        createdAt: params.createdAt ?? now,
        deleted: false,
    };

    await enqueueFeedCommentPatch(scope, commentId, sanitizeForFirestore(commentData));
    logger.cloud(`Feed comment preserved: ${commentId}`);
    return commentId;
}

/**
 * Soft-deletes a feed comment.
 */
export async function deleteFeedComment(scope: ProjectScope, commentId: string): Promise<void> {
    await enqueueFeedCommentPatch(scope, commentId, { deleted: true });
    logger.cloud(`Feed comment soft-delete preserved: ${commentId}`);
}

// ---------------------
// Reaction CRUD
// ---------------------

export interface SetReactionParams {
    id: string;
    targetType: FeedReactionTarget;
    targetId: string;
    userId: string;
    reaction: FeedReactionKind;
    createdAt?: number;
    updatedAt?: number;
}

/**
 * Creates or updates a feed reaction record in Firestore.
 */
export async function setFeedReaction(scope: ProjectScope, params: SetReactionParams): Promise<string> {
    const now = Date.now();
    const reactionData: FeedReaction = {
        id: params.id,
        targetType: params.targetType,
        targetId: params.targetId,
        userId: params.userId,
        reaction: params.reaction,
        createdAt: params.createdAt ?? now,
        updatedAt: params.updatedAt ?? now,
        deleted: false,
    };

    await enqueueFeedReactionPatch(scope, params.id, sanitizeForFirestore(reactionData));
    logger.cloud(`Feed reaction preserved: ${params.id}`);
    return params.id;
}

/**
 * Soft-deletes a feed reaction record.
 */
export async function deleteFeedReaction(
    scope: ProjectScope,
    reactionId: string,
    updatedAt = Date.now()
): Promise<void> {
    await enqueueFeedReactionPatch(scope, reactionId, { deleted: true, updatedAt });
    logger.cloud(`Feed reaction soft-delete preserved: ${reactionId}`);
}

// ---------------------
// Real-time Listeners
// ---------------------

/**
 * Subscribes to feed posts for a project.
 * Returns posts ordered by createdAt descending, limited to 100.
 */
export function subscribeToPosts(
    scope: ProjectScope,
    onUpdate: (posts: FeedPost[]) => void
): Unsubscribe {
    const q = query(
        feedRef(scope),
        orderBy('createdAt', 'desc'),
        limit(100)
    );

    return onSnapshot(q, (snapshot) => {
        const posts: FeedPost[] = snapshot.docs.map(doc => ({
            ...doc.data(),
            id: doc.id,
        }));
        onUpdate(posts);
    }, (error) => {
        console.error('[feed-api] Posts subscription error:', error);
    });
}

/**
 * Subscribes to feed comments for a project.
 * Returns comments ordered by createdAt ascending.
 */
export function subscribeToComments(
    scope: ProjectScope,
    onUpdate: (comments: FeedComment[]) => void
): Unsubscribe {
    const q = query(
        commentsRef(scope),
        orderBy('createdAt', 'asc')
    );

    return onSnapshot(q, (snapshot) => {
        const comments: FeedComment[] = snapshot.docs.map(doc => ({
            ...doc.data(),
            id: doc.id,
        }));
        onUpdate(comments);
    }, (error) => {
        console.error('[feed-api] Comments subscription error:', error);
    });
}

/**
 * Subscribes to feed reactions for a project.
 */
export function subscribeToReactions(
    scope: ProjectScope,
    onUpdate: (reactions: FeedReaction[]) => void
): Unsubscribe {
    const q = query(
        reactionsRef(scope),
        orderBy('updatedAt', 'desc')
    );

    return onSnapshot(q, (snapshot) => {
        const reactions: FeedReaction[] = snapshot.docs.map(doc => ({
            ...doc.data(),
            id: doc.id,
        }));
        onUpdate(reactions);
    }, (error) => {
        console.error('[feed-api] Reactions subscription error:', error);
    });
}
